import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  makeStyles, tokens,
  Field, Input, Textarea, Checkbox, Button, ToggleButton, Spinner,
} from '@fluentui/react-components';
import { API_BASE, PAYSERA_URL } from '../config.js';
import { Heading } from '../theme/primitives';
import { iconMap } from '../theme/icons';
import { stonePalette } from '../theme/brand';
import giftCardImg from '../assets/img/marketing/gift-card.jpg';

const CheckIcon = iconMap.check;
const ErrorIcon = iconMap.error;
const LockIcon = iconMap.lock;
const CreditCardIcon = iconMap.credit_card;
const GiftIcon = iconMap.card_giftcard;
const CalendarIcon = iconMap.calendar_month;

const PRESET_AMOUNTS = [50, 100, 150, 200, 300];

// Plonas apvalkalas ties realiais Fluent `Field`+`Input`/`Textarea` — ta pati
// signatūra (label/rows/...) kaip senojo lokalaus helperio, žr. Checkout.jsx.
function FormField({ label, required, rows, ...props }) {
  return (
    <Field label={label} required={required}>
      {rows
        ? <Textarea resize="none" rows={rows} {...props} />
        : <Input {...props} />}
    </Field>
  );
}

const useStyles = makeStyles({
  root: { backgroundColor: stonePalette[50] },

  // Balta (ne paveldėta stonePalette[50]) — žr. #8: sekcija su nuotrauka
  // turi būti balta visuose šios struktūros puslapiuose.
  hero: {
    backgroundColor: tokens.colorNeutralBackground1,
    paddingTop: tokens.spacingVerticalXXXL, paddingBottom: tokens.spacingVerticalXXXL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Paddingas ant heroInner (ne hero) — kai maxWidth-centravimas IR paddingas
  // yra ant SKIRTINGŲ elementų, susidaro dvigubas centravimas, kuris duoda
  // kitokį kairįjį atitraukimą nei header'io. Sudėjus abu ant to paties
  // elemento, formulė identiška TopBar.jsx/MainMenu.jsx `container` (žr.
  // /admin/bugs "Header logotipas ir language icon turi lygiuoti su
  // puslapio turiniu").
  heroInner: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL, alignItems: 'center',
    '@media (min-width: 900px)': { gridTemplateColumns: '1fr 1fr' },
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  heroEyebrow: { marginBottom: tokens.spacingVerticalS },
  title: { marginBottom: tokens.spacingVerticalM },
  heroText: { fontSize: tokens.fontSizeBase400, color: tokens.colorNeutralForeground2, lineHeight: '1.6', marginBottom: tokens.spacingVerticalL, maxWidth: '32rem' },
  heroPoints: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heroPoint: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground1 },
  heroPointIcon: { color: tokens.colorBrandForeground2, fontSize: '18px', flexShrink: 0 },
  heroImageWrap: {
    borderRadius: tokens.borderRadiusXLarge, overflow: 'hidden', boxShadow: tokens.shadow16,
    aspectRatio: '4 / 3',
  },
  heroImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },

  section: { paddingTop: '4rem', paddingBottom: '4rem', backgroundColor: tokens.colorNeutralBackground2 },
  // Paddingas ant sectionInner (ne section) — ta pati dvigubo centravimo
  // problema, kaip heroInner aukščiau (žr. /admin/bugs "sie trys blokai turi
  // buti tokio pacio plocio kaip ir virsuje esantis turinys"). 80rem — tas
  // pats standartinis puslapio konteinerio plotis, kaip /meistro-uzsakymas
  // (žr. Carpenter.jsx), kad apatiniai 3 blokeliai būtų vienodo pločio abiejuose puslapiuose.
  sectionInner: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },

  grid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL,
    '@media (min-width: 1024px)': { gridTemplateColumns: '1fr 1fr 1fr' },
  },

  card: {
    backgroundColor: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge, padding: tokens.spacingHorizontalXXL,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL,
  },
  cardTitle: {
    fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1,
    paddingBottom: tokens.spacingVerticalM, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  recipientBlock: { paddingTop: tokens.spacingVerticalS, borderTop: `1px solid ${tokens.colorNeutralStroke2}`, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  recipientLabel: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.05em' },

  amountGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: tokens.spacingHorizontalS },
  // Realus Fluent <ToggleButton> jau suteikia aukštį/kraštus/appearance
  // pagal Fluent2 standartą (žr. #5) — čia tik plotis per grid langelį.
  amountBtn: { width: '100%' },

  customLabel: { display: 'block', fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground2, marginBottom: tokens.spacingVerticalXS, textTransform: 'uppercase', letterSpacing: '0.05em' },
  customNote: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS },

  amountSummary: { backgroundColor: tokens.colorBrandBackground2, borderRadius: tokens.borderRadius3XLarge, padding: tokens.spacingHorizontalL },
  amountSummaryLabel: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalXXS },
  amountSummaryValue: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightBold, color: tokens.colorBrandForeground2 },

  featureList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2 },
  featureRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  featureIcon: { fontSize: '14px', color: tokens.colorBrandForeground2, flexShrink: 0 },

  summaryBox: { backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadius3XLarge, padding: tokens.spacingHorizontalL, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, fontSize: tokens.fontSizeBase300 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', color: tokens.colorNeutralForeground2 },
  summaryValue: { fontWeight: tokens.fontWeightSemibold },
  summaryDivider: { height: '1px', backgroundColor: tokens.colorNeutralStroke2 },
  summaryTotalRow: { display: 'flex', justifyContent: 'space-between', fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase400 },
  summaryTotalValue: { color: tokens.colorBrandForeground2 },

  paymentLabel: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: tokens.spacingVerticalXS },
  paymentBox: { borderRadius: tokens.borderRadiusXLarge, border: `1px solid ${tokens.colorNeutralStroke2}`, padding: tokens.spacingHorizontalM, display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  paymentIcon: { fontSize: '20px', color: tokens.colorBrandForeground2 },
  paymentText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2 },

  errorBox: {
    display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalS, padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorStatusDangerBackground1, border: `1px solid ${tokens.colorStatusDangerBorder1}`,
    borderRadius: tokens.borderRadiusXLarge, fontSize: tokens.fontSizeBase300, color: tokens.colorStatusDangerForeground1,
  },
  errorIcon: { fontSize: '15px', flexShrink: 0, marginTop: '2px' },

  agreeRow: { display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalS, marginTop: 'auto', cursor: 'pointer' },
  agreeLabel: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, lineHeight: '1.5' },

  submitBtn: { width: '100%', justifyContent: 'center', paddingTop: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalL },
});

function GiftCard() {
  const { t } = useTranslation();
  const s = useStyles();
  useEffect(() => { document.title = `${t('giftcard.title')} | Fitit`; }, [t]);

  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);

  const [form, setForm] = useState({
    buyerName: '',
    buyerEmail: '',
    recipientName: '',
    message: '',
  });

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const finalAmount = useCustom ? parseFloat(customAmount) || 0 : amount;

  const submitPayseraForm = (name, email, amountEur) => {
    const f = document.createElement('form');
    f.method = 'POST';
    f.action = PAYSERA_URL;
    [['name', name], ['email', email], ['amount', Math.round(amountEur * 100)]].forEach(([k, v]) => {
      const inp = document.createElement('input'); inp.type = 'hidden'; inp.name = k; inp.value = v; f.appendChild(inp);
    });
    document.body.appendChild(f); f.submit();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.buyerName || !form.buyerEmail) { setError(t('giftcard.error_required')); return; }
    if (finalAmount < 10) { setError(t('giftcard.error_min_amount')); return; }
    if (!agreed) { setError(t('giftcard.error_agree')); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/gift-cards/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, amount: finalAmount }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || t('giftcard.generic_error')); }
      submitPayseraForm(form.buyerName, form.buyerEmail, finalAmount);
    } catch (err) { setError(err.message); setLoading(false); }
  };

  return (
    <div className={s.root}>

      {/* Hero */}
      <div className={s.hero}>
        <div className={s.heroInner}>
          <div>
            <Heading as="h1" variant="h1" className={s.title}>{t('giftcard.title')}</Heading>
            <p className={s.heroText}>
              {t('giftcard.hero_text')}
            </p>
            <div className={s.heroPoints}>
              <div className={s.heroPoint}><GiftIcon className={s.heroPointIcon} />{t('giftcard.hero_point_0')}</div>
              <div className={s.heroPoint}><CalendarIcon className={s.heroPointIcon} />{t('giftcard.hero_point_1')}</div>
              <div className={s.heroPoint}><CheckIcon className={s.heroPointIcon} />{t('giftcard.hero_point_2')}</div>
            </div>
          </div>
          <div className={s.heroImageWrap}>
            <img src={giftCardImg} alt={t('giftcard.img_alt')} className={s.heroImage} />
          </div>
        </div>
      </div>

      {/* Form */}
      <section className={s.section}>
        <div className={s.sectionInner}>
          <form onSubmit={handleSubmit}>
            <div className={s.grid}>

              {/* Column 1 — Buyer */}
              <div className={s.card}>
                <Heading as="h3" variant="h3" className={s.cardTitle}>
                  {t('giftcard.section_buyer')}
                </Heading>
                <FormField label={t('giftcard.buyer_name')} name="buyerName" value={form.buyerName} onChange={handleChange} placeholder="Jonas" required />
                <FormField label={t('giftcard.buyer_email')} name="buyerEmail" type="email" value={form.buyerEmail} onChange={handleChange} placeholder="jonas@pvz.lt" required />

                <div className={s.recipientBlock}>
                  <p className={s.recipientLabel}>{t('giftcard.recipient_section')}</p>
                  <FormField label={t('giftcard.recipient_name')} name="recipientName" value={form.recipientName} onChange={handleChange} placeholder="Ona" />
                  <FormField label={t('giftcard.message')} name="message" value={form.message} onChange={handleChange} placeholder="Sveikiname ir linkime..." rows={3} />
                </div>
              </div>

              {/* Column 2 — Amount */}
              <div className={s.card}>
                <Heading as="h3" variant="h3" className={s.cardTitle}>
                  {t('giftcard.section_amount')}
                </Heading>

                <div className={s.amountGrid}>
                  {PRESET_AMOUNTS.map(a => (
                    <ToggleButton
                      key={a}
                      appearance={!useCustom && amount === a ? 'primary' : 'outline'}
                      checked={!useCustom && amount === a}
                      onClick={() => { setAmount(a); setUseCustom(false); }}
                      className={s.amountBtn}
                    >
                      {a} €
                    </ToggleButton>
                  ))}
                  <ToggleButton
                    appearance={useCustom ? 'primary' : 'outline'}
                    checked={useCustom}
                    onClick={() => setUseCustom(true)}
                    className={s.amountBtn}
                  >
                    {t('giftcard.other')}
                  </ToggleButton>
                </div>

                {useCustom && (
                  <div>
                    <label className={s.customLabel}>{t('giftcard.custom_label')}</label>
                    <Input
                      type="number"
                      min="10"
                      max="2000"
                      step="1"
                      value={customAmount}
                      onChange={e => setCustomAmount(e.target.value)}
                      placeholder="Pvz. 75"
                      style={{ width: '100%' }}
                    />
                    <p className={s.customNote}>{t('giftcard.custom_min')}</p>
                  </div>
                )}

                <div className={s.amountSummary}>
                  <p className={s.amountSummaryLabel}>{t('giftcard.amount_label')}</p>
                  <p className={s.amountSummaryValue}>{finalAmount > 0 ? `${finalAmount} €` : '—'}</p>
                </div>

                <div className={s.featureList}>
                  {[
                    t('giftcard.feature_1'),
                    t('giftcard.feature_2'),
                    t('giftcard.feature_3'),
                  ].map(feat => (
                    <div key={feat} className={s.featureRow}>
                      <CheckIcon className={s.featureIcon} />
                      {feat}
                    </div>
                  ))}
                </div>
              </div>

              {/* Column 3 — Summary & Pay */}
              <div className={s.card}>
                <Heading as="h3" variant="h3" className={s.cardTitle}>
                  {t('giftcard.section_payment')}
                </Heading>

                <div className={s.summaryBox}>
                  <div className={s.summaryRow}>
                    <span>{t('giftcard.item_label')}</span>
                    <span className={s.summaryValue}>{finalAmount > 0 ? `${finalAmount} €` : '—'}</span>
                  </div>
                  <div className={s.summaryDivider} />
                  <div className={s.summaryTotalRow}>
                    <span>{t('giftcard.total')}</span>
                    <span className={s.summaryTotalValue}>{finalAmount > 0 ? `${finalAmount} €` : '—'}</span>
                  </div>
                </div>

                <div>
                  <p className={s.paymentLabel}>{t('common.payment_label')}</p>
                  <div className={s.paymentBox}>
                    <CreditCardIcon className={s.paymentIcon} />
                    <span className={s.paymentText}>{t('common.payment_paysera')}</span>
                  </div>
                </div>

                {error && (
                  <div className={s.errorBox}>
                    <ErrorIcon className={s.errorIcon} />
                    {error}
                  </div>
                )}

                <Checkbox
                  className={s.agreeRow}
                  checked={agreed}
                  onChange={(_, data) => setAgreed(!!data.checked)}
                  label={{ children: t('giftcard.agree'), className: s.agreeLabel }}
                />

                <Button type="submit" appearance="primary" size="large" disabled={loading || finalAmount < 10} className={s.submitBtn}>
                  {loading
                    ? <Spinner size="tiny" />
                    : <>
                        <LockIcon fontSize={18} />
                        {t('giftcard.btn_pay', { amount: finalAmount > 0 ? finalAmount : '' })}
                      </>
                  }
                </Button>
              </div>

            </div>
          </form>
        </div>
      </section>

    </div>
  );
}

export default GiftCard;
